import React, { useEffect, useRef, useState, useMemo } from "react";
import Pusher from "pusher-js";
import { useSearchParams } from "react-router-dom";

export const SocketConnectionTest = () => {
    const [searchParams] = useSearchParams();
    const [messages, setMessages] = useState([]);
    const [connectionStatus, setConnectionStatus] = useState("Desconectado");

    // Leer parámetros de URL
    const attrs = useMemo(() => ({
        vtPusherAppId: searchParams.get("vtPusherAppId") || "2037409",
        vtPusherKey: searchParams.get("vtPusherKey") || "bf3032a9613513a94959",
        vtPusherSecret: searchParams.get("vtPusherSecret") || "dbcccbe2240fad87591",
        vtPusherCluster: searchParams.get("vtPusherCluster") || "eu",
        vtPusherChannel: searchParams.get("vtPusherChannel") || "BOMancity",
        vtPusherEvent: searchParams.get("vtPusherEvent") || "showUserInBackOffice",
    }), [searchParams]);

    const pusherRef = useRef(null);
    const channelRef = useRef(null);

    // Conectar a Pusher
    useEffect(() => {
        const {
            vtPusherKey,
            vtPusherCluster,
            vtPusherChannel,
            vtPusherEvent,
            vtPusherSecret,
            vtPusherAppId,
        } = attrs;

        // Nota importante: secret y appId no se usan en el cliente
        if (vtPusherSecret || vtPusherAppId) {
            console.info(
                "Nota: vtPusherSecret y vtPusherAppId se ignoran en el frontend (solo backend)."
            );
        }

        // Validación mínima para canales públicos
        if (!vtPusherKey || !vtPusherCluster || !vtPusherChannel || !vtPusherEvent) {
            setConnectionStatus("Error: Faltan parámetros requeridos");
            return;
        }

        setConnectionStatus("Conectando...");

        // Limpiar conexiones previas
        if (channelRef.current) {
            try {
                channelRef.current.unbind_all();
                channelRef.current.unsubscribe?.();
            } catch {}
            channelRef.current = null;
        }
        if (pusherRef.current) {
            try {
                pusherRef.current.disconnect();
            } catch {}
            pusherRef.current = null;
        }

        // Crear cliente Pusher
        const pusher = new Pusher(vtPusherKey, {
            cluster: vtPusherCluster,
            forceTLS: true,
        });
        pusherRef.current = pusher;

        const channel = pusher.subscribe(vtPusherChannel);
        channelRef.current = channel;

        // Logs de suscripción
        const onSucceeded = () => {
            const successMsg = `✅ Suscrito a "${vtPusherChannel}". Escuchando evento "${vtPusherEvent}"...`;
            console.log(successMsg);
            setConnectionStatus("Conectado");
            setMessages((prev) => [
                ...prev,
                {
                    type: "success",
                    timestamp: new Date().toLocaleTimeString(),
                    message: successMsg,
                },
            ]);
        };

        const onError = (status) => {
            const errorMsg = `❌ Error de suscripción: ${JSON.stringify(status)}`;
            console.error(errorMsg);
            setConnectionStatus("Error");
            setMessages((prev) => [
                ...prev,
                {
                    type: "error",
                    timestamp: new Date().toLocaleTimeString(),
                    message: errorMsg,
                },
            ]);
        };

        const onData = (data) => {
            // En lugar de raiseEvent, hacemos console.log y mostramos en la interfaz
            console.log("📨 Mensaje recibido:");
            console.log("Canal:", vtPusherChannel);
            console.log("Evento:", vtPusherEvent);
            console.log("Datos:", data);
            console.log("---");

            // Agregar a la lista de mensajes mostrados en la UI
            setMessages((prev) => [
                ...prev,
                {
                    type: "data",
                    timestamp: new Date().toLocaleTimeString(),
                    channel: vtPusherChannel,
                    event: vtPusherEvent,
                    data: data,
                },
            ]);
        };

        channel.bind("pusher:subscription_succeeded", onSucceeded);
        channel.bind("pusher:subscription_error", onError);
        channel.bind(vtPusherEvent, onData);

        // Cleanup al desmontar
        return () => {
            try {
                channel.unbind("pusher:subscription_succeeded", onSucceeded);
                channel.unbind("pusher:subscription_error", onError);
                channel.unbind(vtPusherEvent, onData);
                pusher.unsubscribe(vtPusherChannel);
                pusher.disconnect();
            } catch {}
            channelRef.current = null;
            pusherRef.current = null;
            setConnectionStatus("Desconectado");
        };
    }, [attrs]);

    // Función para limpiar mensajes
    const clearMessages = () => {
        setMessages([]);
    };

    return (
        <div style={{
            width: "100%",
            height: "100vh",
            padding: "20px",
            backgroundColor: "#1e1e1e",
            color: "#fff",
            fontFamily: "monospace",
            overflow: "auto"
        }}>
            <h1 style={{ marginBottom: "20px", color: "#ED0029" }}>Socket Connection Test</h1>

            {/* Estado de conexión */}
            <div style={{
                marginBottom: "20px",
                padding: "10px",
                backgroundColor: "#2d2d2d",
                borderRadius: "5px",
                borderLeft: `4px solid ${
                    connectionStatus === "Conectado" ? "#00ff00" :
                    connectionStatus === "Error" ? "#ff0000" :
                    connectionStatus === "Conectando..." ? "#ffff00" :
                    "#808080"
                }`
            }}>
                <strong>Estado:</strong> {connectionStatus}
            </div>

            {/* Configuración */}
            <div style={{
                marginBottom: "20px",
                padding: "15px",
                backgroundColor: "#2d2d2d",
                borderRadius: "5px"
            }}>
                <h2 style={{ marginBottom: "10px", fontSize: "18px" }}>Configuración:</h2>
                <div style={{ fontSize: "14px", lineHeight: "1.8" }}>
                    <div><strong>App ID:</strong> {attrs.vtPusherAppId}</div>
                    <div><strong>Key:</strong> {attrs.vtPusherKey}</div>
                    <div><strong>Cluster:</strong> {attrs.vtPusherCluster}</div>
                    <div><strong>Channel:</strong> {attrs.vtPusherChannel}</div>
                    <div><strong>Event:</strong> {attrs.vtPusherEvent}</div>
                </div>
            </div>

            {/* Ejemplo de URL */}
            <div style={{
                marginBottom: "20px",
                padding: "15px",
                backgroundColor: "#2d2d2d",
                borderRadius: "5px"
            }}>
                <h2 style={{ marginBottom: "10px", fontSize: "18px" }}>Ejemplo de URL:</h2>
                <code style={{
                    display: "block",
                    padding: "10px",
                    backgroundColor: "#1e1e1e",
                    borderRadius: "3px",
                    fontSize: "12px",
                    wordBreak: "break-all"
                }}>
                    /socketTest?vtPusherKey=YOUR_KEY&vtPusherCluster=YOUR_CLUSTER&vtPusherChannel=YOUR_CHANNEL&vtPusherEvent=YOUR_EVENT
                </code>
            </div>

            {/* Botón para limpiar mensajes */}
            <button
                onClick={clearMessages}
                style={{
                    marginBottom: "20px",
                    padding: "10px 20px",
                    backgroundColor: "#ED0029",
                    color: "#fff",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "14px"
                }}
            >
                Limpiar Mensajes
            </button>

            {/* Lista de mensajes */}
            <div style={{
                padding: "15px",
                backgroundColor: "#2d2d2d",
                borderRadius: "5px"
            }}>
                <h2 style={{ marginBottom: "10px", fontSize: "18px" }}>
                    Mensajes ({messages.length}):
                </h2>
                <div style={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    fontSize: "14px"
                }}>
                    {messages.length === 0 ? (
                        <div style={{ color: "#808080", fontStyle: "italic" }}>
                            No hay mensajes aún. Esperando eventos...
                        </div>
                    ) : (
                        messages.map((msg, index) => (
                            <div
                                key={index}
                                style={{
                                    marginBottom: "15px",
                                    padding: "10px",
                                    backgroundColor: "#1e1e1e",
                                    borderRadius: "3px",
                                    borderLeft: `4px solid ${
                                        msg.type === "success" ? "#00ff00" :
                                        msg.type === "error" ? "#ff0000" :
                                        "#0099ff"
                                    }`
                                }}
                            >
                                <div style={{ marginBottom: "5px", color: "#808080" }}>
                                    [{msg.timestamp}]
                                </div>
                                {msg.type === "data" ? (
                                    <>
                                        <div><strong>Canal:</strong> {msg.channel}</div>
                                        <div><strong>Evento:</strong> {msg.event}</div>
                                        <div style={{ marginTop: "5px" }}>
                                            <strong>Datos:</strong>
                                            <pre style={{
                                                marginTop: "5px",
                                                padding: "10px",
                                                backgroundColor: "#000",
                                                borderRadius: "3px",
                                                overflow: "auto",
                                                fontSize: "12px"
                                            }}>
                                                {JSON.stringify(msg.data, null, 2)}
                                            </pre>
                                        </div>
                                    </>
                                ) : (
                                    <div>{msg.message}</div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};
